import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { useRelationshipTasks } from 'services/repositories/tasks/tasks';
import { useTaskVariables } from 'services/tasks/tasks';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { getTradePartner } from 'support/helpers/relationships/relationships';
import { TaskDTO } from 'support/types';
import { useConnectionNavigation } from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { DisplayModes } from 'components/DataTable/Types';
import { AlertSection } from 'components/Display/Alert';
import { Alert } from 'components/Display/Alert/Alert';
import { TaskSlideOver as TaskSlideOverBase } from 'components/TaskSlideOver/TaskSlideOver';

import { useTaskSubmission } from './hooks/useTaskSubmission';

export const TaskSlideOver = ({ open }: { open: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerId, relationshipId, taskId } = useParams<{
    partnerId: string;
    relationshipId: string;
    taskId: string;
  }>();
  const { routeTo } = useConnectionNavigation();

  const { data: currentPartner, isLoading: isCurrentPartnerLoading } = useCurrentPartner();
  const { data: relationship, isLoading: isLoadingRelationship } = useRelationship({
    variables: { relationshipId },
    keepPreviousData: true,
    enabled: Boolean(relationshipId),
  });
  const { data: relationshipTasks, isLoading: isLoadingTasks } = useRelationshipTasks({
    variables: { relationshipId },
    enabled: Boolean(relationshipId),
  });
  const task = relationshipTasks?.data.find((t) => t.id === taskId);

  const isLoading = isCurrentPartnerLoading || isLoadingRelationship || isLoadingTasks;
  const otherPartner = relationship && currentPartner && getTradePartner(relationship, currentPartner.id);
  const mode = getTaskMode(task, currentPartner?.id);
  const variables = useTaskVariables(relationship, task?.responsible_partner?.id);

  const isCompletedByCurrentPartner =
    task?.state === 'COMPLETED' && task?.responsible_partner?.id === currentPartner?.id;

  const handleClose = useCallback(
    () =>
      navigate(
        routeTo('relationshipDetails', {
          partnerId: partnerId!,
          relationshipId: relationshipId!,
        }),
      ),
    [navigate, routeTo, partnerId, relationshipId],
  );

  const handleSuccess = () => {
    const isLastPendingTask =
      relationshipTasks?.data.filter((t) => t.state === 'PENDING' || t.state === 'BLOCKED').length === 1 &&
      (task?.state === 'PENDING' || task?.state === 'BLOCKED');
    if (isLastPendingTask) {
      addNotification(
        {
          title: t('connections:relationshipDetails.task.allTasksCompleted.title'),
          subtitle: t('connections:relationshipDetails.task.allTasksCompleted.description'),
        },
        NotificationType.success,
      );
    }
    handleClose();
  };

  const { submitTask, isSubmitting } = useTaskSubmission({
    relationshipId: relationshipId!,
    taskId: taskId!,
    onSuccess: handleSuccess,
  });

  return (
    <TaskSlideOverBase
      open={open}
      onClose={handleClose}
      isLoading={isLoading}
      partnerName={otherPartner?.name}
      partnerLogoUrl={otherPartner?.logo_url}
      task={task}
      variables={variables}
      mode={mode}
      isSubmitting={isSubmitting}
      relationshipId={relationship?.id}
      onSubmit={submitTask}
      topBanner={
        task?.state === 'BLOCKED' && task.rejection_reason ? (
          <Alert severity="error" fullWidth title={t('connections:relationshipDetails.task.blockedBanner.title')}>
            <p>{task.rejection_reason}</p>
          </Alert>
        ) : undefined
      }
      extraContent={
        isCompletedByCurrentPartner ? (
          <Alert severity="success">
            <AlertSection
              severity="success"
              title={t('connections:relationshipDetails.task.completedBanner.description')}
            ></AlertSection>
          </Alert>
        ) : undefined
      }
    />
  );
};

const getTaskMode = (task: TaskDTO | undefined, currentPartnerId: string | undefined): DisplayModes => {
  if (!task || !currentPartnerId || task.responsible_partner?.id !== currentPartnerId) {
    return DisplayModes.view;
  }

  if (task.state === 'PENDING') {
    return DisplayModes.create;
  }

  if (task.state === 'BLOCKED') {
    return DisplayModes.edit;
  }

  return DisplayModes.view;
};
